import React, {
  MouseEvent, useContext, useEffect, useState
} from 'react';
import { useMutation } from '@apollo/client';

import Variations from '@components/shared/ProductCard/Variations';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import getVendorVariantAnalyticsPayload from '@lib/vendorVariant/getVendorVariantAnalyticsPayload';
import UserContext from '@context/User/user-context';
import ExploreProductModalContext from '@context/Modal/explore-product-modal-context';
import { SrcSet } from '@lib/image';
import breakpoints from '@constants/breakpoints';
import getProductUrl from '@lib/vendorVariant/getProductUrl';
import ButtonSecondary from '@components/shared/buttons/ButtonSecondary/ButtonSecondary';
import { vendorVariantFields, vendorVariantFragmentName } from '@graphql/VendorVariant/fragments/vendorVariantFields.graphql';
import AddedToCartConfirmationModalContext from '@context/Modal/added-to-cart-confirmation-modal-context';
import { isOverstockVendor } from '@components/shared/shop/model/Vendors';
import ArrowLeft from '@components/shared/assets/arrow-left.svg';
import classNames from 'classnames';
import { LazyImage } from 'react-lazy-images';
import SaleBanner from '@components/shared/shop/ShopProductCard/SaleBanner/SaleBanner';
import { useTheme } from '@components/theme';
import { useRouter } from 'next/router';
import getProductVanityPath from '@lib/vendorVariant/getProductVanityPath';
import setProductUserOpinion from './graphql/setProductUserOpinion.graphql';
import SavedBadge from './SavedBadge/SavedBadge';
import UserOpinion from './UserOpinion/UserOpinion';
import ProductCardActions from '../ProductCardActions/ProductCardActions';
import styles from './ProductCard.module.scss';
import AddToCartButton from './AddToCartButton/AddToCartButton';
import Skeleton from '../Skeleton/Skeleton';
import PlaceholderImage from '../images/atoms/Image/PlaceholderImage';
import SaleTreatmentBanner from '../shop/SaleTreatmentBanner/SaleTreatmentBanner';
import RegularSalePriceDisplay from '../prices/RegularSalePriceDisplay/RegularSalePriceDisplay';
import SrcSetPicture from '../Picture/SrcSetPicture';
import AspectRatioBox from '../AspectRatioBox/AspectRatioBox';
import DesignAwardBadge from '../DesignAwards/DesignAwardBadge';
import FreeShippingBadge from './FreeShippingBadge/FreeShippingBadge';

interface IProductCardProps {
  className?: string;
  analytics: {
    listId?: string;
    location: string;
  };
  boardId?: number;
  product: any;
  alternate?: boolean;
  listPosition: number;
  pullSavedForLaterRight?: boolean;
  showLikeButtons?: boolean;
  showActionMenu?: boolean;
  showSaleBanner?: boolean;
  onlyImages?: boolean;
  recommendedQuantity?: number;
  roomId?: number;
  showBoardDetails?: boolean;
  size?: 'x-small' | 'small' | 'medium' | 'large' | 'x-large';
  hideVariationsCount?: boolean;
  loadImageEagerly?: boolean;
  alternatesCount?: number;
  showingAlternates?: boolean;
  onShowAlternates?: (primaryId: string) => void;
  imageIndex?: number;
  imageAspectRatio?: number;
  isClickable?: boolean;
  showFullDetailsPage?: boolean;
  isShopProductCard?: boolean;
  onEDesignPage?: boolean;
  showMaxDiscountText?: boolean;
}

export default function ProductCard({
  className = '',
  analytics,
  boardId,
  product,
  alternate = false,
  listPosition,
  pullSavedForLaterRight = false,
  showLikeButtons = false,
  showActionMenu = false,
  showSaleBanner = false,
  recommendedQuantity,
  size = 'large',
  roomId,
  loadImageEagerly,
  hideVariationsCount = false,
  alternatesCount = 0,
  showingAlternates,
  onShowAlternates,
  imageIndex,
  imageAspectRatio = 1,
  isClickable = true,
  showFullDetailsPage = false,
  isShopProductCard = false,
  onEDesignPage = false,
}: IProductCardProps) {
  const theme = useTheme();
  const {
    user,
  } = useContext(UserContext);
  const {
    open: productModalOpen,
    isClosed: isProductModalClosed,
    isDoneAddingToCart,
    onDoneAddingToCart,
  } = useContext(ExploreProductModalContext);
  const { open: openAddedToCartConfirmation } = useContext(AddedToCartConfirmationModalContext);
  const [triggeredProductModal, setTriggeredProductModal] = useState(false);
  const [userOpinion, setUserOpinion] = useState(product.userOpinion?.opinion);

  const [updateOpinion, { loading: updatingOpinion }] = useMutation(setProductUserOpinion, {
    update(cache, { data: { createUpdateUserOpinionVendorVariant: opinion } }) {
      const fragmentId = `VendorVariantModel:${opinion.vendorVariantId}`;
      const vv = cache.readFragment({
        id: fragmentId,
        fragment: vendorVariantFields,
        fragmentName: vendorVariantFragmentName,
      });
      cache.writeFragment({
        id: fragmentId,
        fragment: vendorVariantFields,
        fragmentName: vendorVariantFragmentName,
        data: {
          // @ts-ignore
          ...vv,
          userOpinion: {
            ...opinion
          }
        }
      });
    }
  });

  const inStock = product.availability === 'in_stock';
  const unavailable = product.availability === 'unavailable' || product.availability === 'unavailable_add_to_cart';
  const { isOnSale } = product;
  const hasTieredDiscount = !!product.vendorTieredDiscountCode;
  const isBackOrdered = product.availability === 'back_ordered';
  const showCTAs = !isShopProductCard;
  const hasRecommendedQuantity = !!recommendedQuantity && recommendedQuantity > 1;

  const router: NextRouter = useRouter();

  const handleTrackExploreProductClicked = () => {
    analyticsTrack(
      'product_clicked',
      {
        ...getVendorVariantAnalyticsPayload(product),
        clickType: 'modal',
        listId: analytics.listId || '',
        listPosition: listPosition + 1 || '',
        source: analytics.location
      },
    );
  };

  const handleProductClick = (e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    let readOnly = false;

    if (isOverstockVendor(parseInt(product.vendor.id, 10))) {
      window.open(product.weblink, '_blank');
      return;
    }

    if (user) {
      readOnly = user && (user.role === 'admin' || user.role === 'designer');
    }

    if (showFullDetailsPage) {
      router.push(getProductVanityPath(product));
    } else {
      setTriggeredProductModal(true);
      productModalOpen({
        hideSave: true,
        readOnly,
        initialVendorVariantId: Number(product.id),
        source: analytics.location,
        sourceBoardId: boardId,
        analyticsListId: analytics?.listId,
      });
    }

    handleTrackExploreProductClicked();
  };

  useEffect(() => {
    if (triggeredProductModal && isProductModalClosed && isDoneAddingToCart) {
      openAddedToCartConfirmation({ vendorVariant: product });
      onDoneAddingToCart(false);
      setTriggeredProductModal(false);
    }
  }, [isProductModalClosed, isDoneAddingToCart]);

  const handleUserOpinionChange = async (newOpinion: number) => {
    if (!updatingOpinion) {
      await updateOpinion({
        variables: {
          vendorVariantId: product.id,
          opinion: newOpinion || null,
        },
      });

      setUserOpinion(newOpinion);
    }
  };

  const handleShowAlternates = (productId: string) => {
    if (!onShowAlternates) return;
    onShowAlternates(showingAlternates ? '' : productId);
  };

  const productUrl = getProductUrl(product, { sourceBoardId: boardId });

  const imageUrls = getProductImageSrcSets(
    (imageIndex && product.images[imageIndex]?.uri) || product.image?.uri,
    imageAspectRatio,
    isShopProductCard
  );

  let containerClassName;
  if (isShopProductCard) {
    containerClassName = styles.ShopProductCard;
  } else if (onEDesignPage) {
    containerClassName = styles.EDesignProductCard;
  } else {
    containerClassName = styles.ProductCard;
  }

  let imageWrapperClassName;
  if (isShopProductCard) {
    imageWrapperClassName = styles.ShopImageWrapper;
  } else if (onEDesignPage) {
    imageWrapperClassName = styles.EDesignImageWrapper;
  } else {
    imageWrapperClassName = styles.ImageWrapper;
  }

  let productTitleClassName;
  if (isShopProductCard) {
    productTitleClassName = styles.ProductTitle;
  } else if (onEDesignPage) {
    productTitleClassName = `${styles.ProductTitle} ${styles.EDesignProductTitle}`;
  } else {
    productTitleClassName = `${styles.ProductTitle} ${styles.Core3ProductTitle}`;
  }

  const altText = product.title?.length > 3 ? product.title : '';

  const content = (
    <>
      <div className={imageWrapperClassName}>
        <AspectRatioBox
          className={isShopProductCard ? styles.ShopImageContainer : ''}
          aspectRatio={imageAspectRatio}
        >
          {hasTieredDiscount && showSaleBanner && (inStock || isBackOrdered) && (
            <SaleTreatmentBanner
              vendorTieredDiscountCode={product.vendorTieredDiscountCode}
              className={styles.SaleBanner}
            />
          )}
          {!hasTieredDiscount && isOnSale && showSaleBanner && (inStock || isBackOrdered) && (
            <SaleBanner
              className={styles.SaleBanner}
              backgroundColor={theme.colors.shade6}
              product={product}
            />
          )}
          {!!product.place && (
            <DesignAwardBadge place={product.place} className={styles.DesignAwardBadge} />
          )}
          <SrcSetPicture
            srcSets={imageUrls}
            defaultWidth={1280}
            render={({ imgProps }) => (
              <LazyImage
                {...imgProps}
                alt={altText}
                loadEagerly={loadImageEagerly}
                actual={({ imageProps }) => {
                  return (
                    <>
                      {alternate && (
                        <div className={styles.AlternateOverlay}>Designer Alternate</div>
                      )}
                      <SavedBadge
                        className={`${styles.SavedForLater}${pullSavedForLaterRight ? ` ${styles.PullRight}` : ''}`}
                        vendorVariantId={product.id}
                        sourceBoardId={boardId}
                        pageSource={analytics.location}
                      />
                      {showLikeButtons && (
                        <UserOpinion
                          vendorVariantId={product.id}
                          className={styles.UserOpinion}
                          opinion={userOpinion}
                          onOpinionChange={handleUserOpinionChange}
                        />
                      )}
                      <img {...imageProps} alt={product.title} draggable="false" />
                    </>
                  );
                }}
                loading={() => (
                  <Skeleton />
                )}
                placeholder={({ imageProps, ref }) => (
                  <PlaceholderImage {...imageProps} alt={altText} ref={ref} />
                )}
              />
            )}
          />
        </AspectRatioBox>
      </div>
      <div data-size={size} className={styles.InfoWrapper}>
        {!hideVariationsCount && (
          <Variations
            className={styles.Variations}
            productGroupUrl={product.productGroupUrl}
            count={product.groupCount}
          />
          )}
        <div data-size={size} className={styles.Info}>
          <div data-size={size} className={styles.Info}>
            <div className={styles.ProductVendorTitle}>{product.vendor.name}</div>
            <div className={productTitleClassName}>{product.title}</div>
          </div>
          {showActionMenu && (
            <ProductCardActions
              product={product}
              sourceBoardId={boardId}
              analytics={analytics}
            />
          )}
        </div>
        <div className={styles.PriceContainer}>
          {(inStock || isBackOrdered) && (
            <RegularSalePriceDisplay product={product} needsSmallerFont />
          )}
          {!inStock && !isBackOrdered && (
            <div>
              <span className={`${styles.ProductPrice} ${styles.OutOfStock}`}>{unavailable ? 'Unavailable' : 'Out of stock'}</span>
            </div>
          )}
        </div>
      </div>
      {product?.freeShippingBadge && (
        <FreeShippingBadge className={styles.FreeShippingBadgeLayout} />
      )}
      {hasRecommendedQuantity && (
        <div className={styles.RecommendedQuantity}>
          <span>Recommended Qty: {recommendedQuantity}</span>
        </div>
      )}
    </>
  );

  return (
    <div
      className={`${containerClassName} ${className}`}
      data-size={size}
      data-product-id={product.id}
      data-test="product-card"
    >
      {isClickable ? (
        <a
          href={productUrl}
          onClick={(e) => handleProductClick(e)}
          data-size={size}
          className={styles.ProductCardLink}
        >
          {content}
        </a>
      ) : (<>{ content }</>)}

      {showCTAs && (
        <div className={styles.ActionButtonContainer}>
          {(inStock || !roomId) && (
            <AddToCartButton
              className={styles.ActionButton}
              analytics={analytics}
              boardId={boardId}
              product={product}
            />
          )}

          {unavailable && (
            <ButtonSecondary
              className={styles.ActionButton}
              onClick={handleProductClick}
            >
              View Similar
            </ButtonSecondary>
          )}

          {(!inStock && !!roomId && !unavailable) && (
            <ButtonSecondary
              className={styles.ActionButton}
              onClick={handleProductClick}
            >
              View Similar
            </ButtonSecondary>
          )}
          <button type="button" onClick={handleProductClick} className={styles.ViewAlternatesLink}>View Alternates</button>
        </div>
      )}
      {!!alternatesCount && onShowAlternates && (
        <button
          type="button"
          onClick={() => handleShowAlternates(product.id)}
          className={styles.ViewAlternates}
        >
          View Alternates ({alternatesCount})
          <div className={classNames(
            styles.ArrowWrapper,
            { [styles.Active]: showingAlternates }
          )}
          >
            <ArrowLeft />
          </div>
        </button>
      )}
    </div>
  );
}

function getProductImageSrcSets(imageUrl: string, aspectRatio: number, isShopProductCard = false) {
  const sizes = isShopProductCard
    ? { [SrcSet.DEFAULT]: 963, [`${breakpoints.desktop}`]: 844 }
    : { [SrcSet.DEFAULT]: 284, [`${breakpoints.desktop}`]: 162 };

  const adjustedSizes = Object.entries(sizes).reduce((acc, [key, width]) => {
    acc[key] = [width, Math.round(width / aspectRatio)]; // Dynamically calculate height
    return acc;
  }, {} as Record<string, [number, number]>);

  const imageUrls: Record<string, SrcSet> = {};
  ['webp', 'jpeg'].forEach((format) => {
    imageUrls[format] = new SrcSet({
      sizes: new Map(Object.entries(adjustedSizes)),
      url: imageUrl,
      props: {
        quality: 100,
        fit: 'fit-in', // Ensure the image fills the dimensions properly
        format,
      },
    });
  });
  return imageUrls;
}
